import React, { useEffect } from 'react';

import { AutocompleteCollection, InternalAutocompleteSource } from '@algolia/autocomplete-core';
import { Hit } from '@algolia/client-search';
import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import SearchIcon from '@atlaskit/icon/core/search';

import { Tracking } from '@/analytics/atl-analytics/tracking';
import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useDarkTheme } from '@/libraries/design/utils/dark-theme-provider';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { Cross } from '@/libraries/wpl/foundations/src/icons/basic/cross';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';
import { useFeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';

import {
	ALGOLIA_FACET_KEY,
	ALGOLIA_APP_ID,
	ALGOLIA_SEARCH_API_KEY,
	useA11yDropdownAnnouncement,
	HiddenAriaLiveAnnouncement,
	shouldEnableAlgoliaAnalytics,
	activeSearchIndexV3_2,
} from './auto-complete-helpers';
import AutoCompleteSearchAIResult from './auto-complete-search-ai-result';
import {
	AutoCompleteResultsDropdown,
	DefaultAutocompleteResult,
} from './auto-complete-search-results';
import useAlogoliaAutoComplete from './hooks/use-algolia-autocomplete';

export type AutoCompleteSearchBarProps = {
	isOpenSearch?: boolean;
	setIsOpenSearch?: React.Dispatch<React.SetStateAction<boolean>>;
	isMobileView?: boolean;
	aiEnabled?: boolean;
	onFocus?: () => void;
	readOnly?: boolean;
	parentType?: 'GlobalNav' | 'ProductNav';
	menuPage?: string;
	fullWidth?: boolean;
	matchButtonSize?: boolean;
};

const aaAutoCompContainerPanelStyle = css({
	color: wacToken('color.neutral.dark200'),
	fontFamily: 'inherit',
	fontSize: 16,
	fontWeight: '400',
	lineHeight: '1em',
	margin: '0',
	padding: '0',
	textAlign: 'left',
});

const aaFormStyle = css({
	alignItems: 'center',
	// eslint-disable-next-line local/ensure-wac-token-usage
	border: '1px solid rgba(128, 126, 163, 0.8)',
	borderRadius: '3px',
	display: 'flex',
	lineHeight: '1em',
	margin: '0',
	position: 'relative',
	width: '100%',

	':focus': {
		/* eslint-disable local/ensure-wac-token-usage */
		borderColor: 'rgba(62, 52, 211, 1)',
		boxShadow:
			/* eslint-disable local/ensure-wac-token-usage */
			'rgba(62, 52, 211, 0.2) 0 0 0 2px, inset rgba(62, 52, 211, 0.2) 0 0 0 2px',
		outline: 'medium none currentColor',
	},
});

const aaInputWrapperPrefixStyle = css({
	alignItems: 'center',
	display: 'flex',
	flexShrink: '0',
	height: '42px',
	order: '1',
});

const aaInputWrapperPrefixStyleWithFeatureFlag = css({
	alignItems: 'center',
	display: 'flex',
	flexShrink: '0',
	height: '48px',
	order: 1,
});

const aaLabelStyle = css({
	cursor: 'auto',
	flexShrink: '0',
	height: '100%',
	padding: '0',
	textAlign: 'left',
});

const aaSubmitButtonStyle = css({
	height: '100%',
	paddingInlineStart: 11,
	paddingInlineEnd: 8,
	width: '47px',
	border: 'none',
	background: 'none',
	lineHeight: 1,
});

const aaInputWrapperStyle = css({
	order: 3,
	position: 'relative',
	width: '100%',
	height: 42,
});

const aaInputWrapperStyleWithFeatureFlag = css({
	order: 3,
	position: 'relative',
	width: '100%',
	height: '48px',
});

const aaInputStyle = css({
	appearance: 'none',
	background: 'none',
	border: 0,
	color: wacToken('color.primary.black'),
	font: 'inherit',
	height: '100%',
	padding: 0,
	width: '100%',
	caretColor: wacToken('color.extended.blue700'),

	':focus': {
		borderColor: 'none',
		boxShadow: 'none',
		outline: 'none',
	},
	'::placeholder': {
		color: wacToken('color.neutral.dark500'),
		opacity: 1,
	},
	'::-webkit-search-cancel-button, ::-webkit-search-decoration, ::-webkit-search-results-button, ::-webkit-search-results-decoration':
		{
			appearance: 'none',
		},
});

const aaInputWrapperSuffixStyle = css({
	alignItems: 'center',
	display: 'flex',
	height: 42,
	order: 4,
});

const aaInputWrapperSuffixStyleWithFeatureFlag = css({
	alignItems: 'center',
	display: 'flex',
	height: '48px',
	order: 4,
});

const aaCrossIconStyle = css({
	color: wacToken('color.neutral.light500'),
	':focus': {
		color: wacToken('color.neutral.dark200'),
	},
	':hover': {
		color: wacToken('color.neutral.dark200'),
	},
});

const aaClearButtonStyle = css({
	alignItems: 'center',
	background: 'none',
	border: 0,
	cursor: 'pointer',
	display: 'flex',
	height: '100%',
	margin: 0,
	padding: '0 0 0 32px',
	'&[hidden]': {
		display: 'none',
	},
});

const aaPanelLayoutStyle = css({
	height: '100%',
	margin: 0,
	maxHeight: '650px',
	overflowY: 'auto',
	padding: 0,
	position: 'relative',
	textAlign: 'left',

	':empty': {
		display: 'none',
	},
});

const aaSourceStyle = css({
	margin: 0,
	padding: 0,
	position: 'relative',
	width: '100%',

	':empty': {
		display: 'none',
	},
});

const aaListStyle = css({
	listStyle: 'none',
	margin: 0,
	padding: 0,
});

const SearchBaraaFormaaFormFocusStyle = css({
	border: 'none',
	borderColor: 'transparent',
	outline: 'solid transparent',
	boxShadow: 'none',
});

const searchBarInputWrapperStyle = css({
	position: 'relative',
	display: 'flex',
	verticalAlign: 'middle',
	alignItems: 'center',
	height: '2.25rem',
	borderWidth: 1,
	borderStyle: 'solid',
	borderRadius: '25px 25px 25px 25px',
	padding: '0 16px',
	borderColor: wacToken('color.neutral.light100'),
	backgroundColor: wacToken('color.neutral.light100'),
});

const searchBarInputWrapperDarkThemeStyle = css({
	borderColor: wacToken('color.neutral.dark300'),
	backgroundColor: wacToken('color.neutral.dark300'),
});

const searchBarInputWrapperStyleWithFeatureFlag = css({
	position: 'relative',
	display: 'flex',
	verticalAlign: 'middle',
	alignItems: 'center',
	height: '2.75rem',
	borderStyle: 'solid',
	padding: '0 24px',
	borderRadius: '25px 25px 25px 25px',
	borderColor: wacToken('color.neutral.light100'),
	backgroundColor: wacToken('color.neutral.light100'),
});

const searchBarStyle = css({
	position: 'relative',
	insetBlockEnd: 0,
	marginInlineStart: 20,
	marginInlineEnd: 20,
	width: 'auto',
	maxWidth: '100%',
	zIndex: 5,
	display: 'block',
	justifyContent: 'center',
});

const searchBarMobileStyle = css({
	width: '100%',
	justifyContent: 'center',
	paddingBlockEnd: '24px',
});

const searchBarMobileFullWidthStyle = css({
	width: '100%',
	justifyContent: 'center',
	position: 'relative',
});

const searchBarMobileProductNavStyle = css({
	width: '100%',
	justifyContent: 'center',
	position: 'relative',
	paddingBlockStart: '24px',
	paddingBlockEnd: '24px',
	paddingInlineEnd: '0',
	paddingInlineStart: '0',
});

const searchBarMobileProductNavButtonMatchStyle = css({
	width: '100%',
	justifyContent: 'center',
	position: 'relative',
	marginBlockStart: '8px',
	marginBlockEnd: '8px',
});

const seeAllButtonStyle = css({
	paddingInlineStart: '8px',
	paddingBlockStart: '4px',
});

const aiEnabledSearchIconCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& svg': {
		width: '24px',
		height: '24px',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& svg path': {
		fill: wacToken('color.neutral.light400'),
	},
});

const aiEnabledSearchIconDarkThemeCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& svg path': {
		fill: wacToken('color.neutral.dark500'),
	},
});

const aiEnabledClearButtonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& svg path': {
		fill: wacToken('color.primary.black'),
	},
});

export type ContentHit = Hit<{
	brand: string;
	categories: string[];
	description: string;
	image: string;
	name: string;
	title: string;
	price: number;
	rating: number;
	type: string;
	url: string;
	queryID: string;
	position: number;
}>;

const AutoCompleteSearchBar = ({
	isOpenSearch,
	setIsOpenSearch,
	isMobileView = false,
	aiEnabled = false,
	onFocus,
	readOnly = false,
	parentType = 'GlobalNav',
	menuPage,
	fullWidth = false,
	matchButtonSize = false,
}: // eslint-disable-next-line complexity
AutoCompleteSearchBarProps) => {
	const intl = useIntl();
	const inputRef = React.useRef<HTMLInputElement>(null);
	const formRef = React.useRef<HTMLFormElement>(null);
	const panelRef = React.useRef<HTMLDivElement>(null);
	const lastTrackedQueryRef = React.useRef<string>('');
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useFeatureExposed('wac-search-indexv3', 'Test');

	const { isDarkTheme } = useDarkTheme();

	const hitsPerPageObject = { hitsPerPage: 7 };
	const isAnalyticsEnabled = shouldEnableAlgoliaAnalytics();
	const queryParams = {
		facetFilters: [`locale:${intl.locale ?? 'en'}`],
		clickAnalytics: isAnalyticsEnabled, // enables click analytics
		attributesToRetrieve: ['url', 'title', 'description', 'locale', 'category', 'objectID'],
		...hitsPerPageObject,
		...(aiEnabled
			? {
					restrictSearchableAttributes: ['title'],
					minProximity: 2,
				}
			: {}),
	};

	const { autocompleteState, autocomplete } = useAlogoliaAutoComplete({
		searchIndex: activeSearchIndexV3_2,
		ALGOLIA_FACET_KEY,
		ALGOLIA_APP_ID,
		ALGOLIA_SEARCH_API_KEY,
		queryParams,
		aiEnabled,
	});
	const { getEnvironmentProps } = autocomplete;

	useEffect(() => {
		if (isOpenSearch) {
			if (inputRef.current) {
				inputRef.current.focus();
			}
		}
	}, [isOpenSearch]);

	useEffect(() => {
		if (!formRef.current || !panelRef.current || !inputRef.current) {
			return undefined;
		}

		const { onTouchStart, onTouchMove, onMouseDown } = getEnvironmentProps({
			formElement: formRef.current,
			inputElement: inputRef.current,
			panelElement: panelRef.current,
		});

		window.addEventListener('mousedown', onMouseDown);
		window.addEventListener('touchstart', onTouchStart);
		window.addEventListener('touchmove', onTouchMove);

		return () => {
			window.removeEventListener('mousedown', onMouseDown);
			window.removeEventListener('touchstart', onTouchStart);
			window.removeEventListener('touchmove', onTouchMove);
		};
	}, [getEnvironmentProps, autocompleteState.isOpen]);

	React.useEffect(() => {
		if (!readOnly && inputRef.current) {
			inputRef.current.focus();
		}
	}, [readOnly]);

	const isNotEmptyQuery = (searchQuery: string) => searchQuery.length > 0;

	const searchPagePath = useLocalizedHref(`/search?q=${autocompleteState.query || ''}`);

	const handleSearchFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (isNotEmptyQuery(autocompleteState.query)) {
			createAnalyticsEvent({
				eventName: 'submitSearchForm',
				eventType: 'ui',
				action: 'submitted',
				actionSubject: 'search',
				actionSubjectId: 'globalNavSearchBar',
				eventContainer: 'globalNavSearchBar',
				source: 'globalNav',
				attributes: {
					product: 'search',
					pageUrl: window.location.href,
					pagePath: window.location.pathname,
				},
			}).fire();

			window.location.assign(searchPagePath);
		}
	};

	const handleRenderAutocompleteResult = (
		item: ContentHit,
		source: InternalAutocompleteSource<ContentHit>,
	) => {
		// check if any product landing pages have the same url
		// as the result from Algolia.
		return (
			<DefaultAutocompleteResult
				item={item}
				source={source}
				autocomplete={autocomplete}
				isMobileView={isMobileView}
				aiEnabled={aiEnabled}
			/>
		);
	};

	const { dropdownAnnouncement } = useA11yDropdownAnnouncement({
		resultsCount: autocompleteState.collections
			.filter((collection) => collection.source.sourceId !== 'ai-assistant')
			.reduce((total, collection) => total + collection.items.length, 0),
		searchTerm: autocompleteState.query,
	});

	useEffect(() => {
		if (!autocompleteState.query) {
			lastTrackedQueryRef.current = '';
			return;
		}

		if (lastTrackedQueryRef.current === autocompleteState.query) {
			return;
		}

		// Ensure that the algolia search has finished loading before firing the analytics event
		if (autocompleteState.status === 'loading' || autocompleteState.status === 'stalled') {
			return;
		}

		// Debounce the analytics event to prevent firing until the user has stopped typing
		let queryId = autocompleteState.collections[0]?.items[0]?.queryID || null;
		if (aiEnabled) {
			// AI Result is considered collection 0 so we need to get the query ID from the second collection
			queryId = autocompleteState.collections[1]?.items[0]?.queryID || null;
		}
		const timeoutId = setTimeout(() => {
			createAnalyticsEvent({
				eventName: 'displayAutocompleteSearchResults',
				eventType: 'ui',
				action: 'displayed',
				actionSubject: 'search',
				actionSubjectId: 'globalNavAutoCompleteResults',
				eventContainer: 'globalNavSearchBar',
				source: 'globalNav',
				attributes: {
					product: 'search',
					pageUrl: window.location.href,
					pagePath: window.location.pathname,
					resultsCount: autocompleteState.collections.reduce(
						(total, collection) => total + collection.items.length,
						0,
					),
					queryId: queryId,
					searchQuery: autocompleteState.query,
					searchIndex: 'v3',
				},
			}).fire();

			lastTrackedQueryRef.current = autocompleteState.query;
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [
		autocompleteState.query,
		autocompleteState.collections,
		autocompleteState.status,
		createAnalyticsEvent,
	]);

	useEffect(() => {
		if (autocompleteState.isOpen && aiEnabled) {
			Tracking.autoTrackLinks(true);
		}
	}, [autocompleteState.isOpen, aiEnabled]);

	return (
		<TypographyStyles>
			<div
				className="aa-Autocomplete"
				css={[
					aaAutoCompContainerPanelStyle,
					isMobileView && (parentType === 'ProductNav' || parentType === 'GlobalNav')
						? matchButtonSize
							? searchBarMobileProductNavButtonMatchStyle
							: fullWidth
								? parentType === 'ProductNav'
									? searchBarMobileProductNavStyle
									: searchBarMobileFullWidthStyle
								: searchBarMobileStyle
						: searchBarStyle,
				]}
				data-testid="aa-Autocomplete"
			>
				<form
					ref={formRef}
					className="aa-Form"
					css={[
						aaFormStyle,
						SearchBaraaFormaaFormFocusStyle,
						isMobileView &&
							(parentType === 'GlobalNav' || parentType === 'ProductNav') &&
							menuPage === 'search' &&
							css({ paddingBlockEnd: '24px' }),
						isMobileView &&
							parentType === 'ProductNav' &&
							(fullWidth || matchButtonSize) &&
							css({ height: '36px' }),
					]}
					{...autocomplete.getFormProps({
						inputElement: inputRef.current,
					})}
					onSubmit={handleSearchFormSubmit}
				>
					<div
						className="aa-InputWrapperPrefix"
						css={[
							isMobileView ? aaInputWrapperPrefixStyleWithFeatureFlag : aaInputWrapperPrefixStyle,
							isMobileView &&
								parentType === 'ProductNav' &&
								(fullWidth || matchButtonSize) &&
								css({ height: '36px' }),
						]}
					/>
					<div
						className="aa-InputWrapper"
						css={[
							isMobileView ? aaInputWrapperStyleWithFeatureFlag : aaInputWrapperStyle,
							isMobileView ? searchBarInputWrapperStyleWithFeatureFlag : searchBarInputWrapperStyle,
							isDarkTheme && searchBarInputWrapperDarkThemeStyle,
							isMobileView &&
								parentType === 'ProductNav' &&
								(fullWidth || matchButtonSize) &&
								css({ height: '36px' }),
						]}
					>
						<input
							className="aa-Input"
							aria-label="Search by Keywords"
							css={[aaInputStyle]}
							data-testid="aa-Input"
							ref={inputRef}
							readOnly={readOnly}
							{...autocomplete.getRootProps({})}
							{...autocomplete.getInputProps({
								inputElement: inputRef.current,
								onFocus: () => {
									onFocus?.();
								},
							})}
							aria-labelledby={undefined}
						/>
						<label
							className="aa-Label"
							css={[aaLabelStyle]}
							{...autocomplete.getLabelProps({})}
							htmlFor="autocomplete"
						/>
						<button
							className="aa-SubmitButton"
							css={[
								aaSubmitButtonStyle,
								aiEnabled && aiEnabledSearchIconCss,
								isDarkTheme && aiEnabledSearchIconDarkThemeCss,
							]}
							data-testid="search-submit-button"
							aria-label={intl.formatMessage({
								id: 'globalnav.search.submit-button',
								defaultMessage: 'Submit search',
							})}
							type="submit"
							title="Submit"
							disabled={!isNotEmptyQuery(autocompleteState.query)}
						>
							<SearchIcon label={'search label'} size="medium" />
						</button>
					</div>
					<div
						className="aa-InputWrapperSuffix"
						css={[
							isMobileView ? aaInputWrapperSuffixStyleWithFeatureFlag : aaInputWrapperSuffixStyle,
							isMobileView &&
								parentType === 'ProductNav' &&
								(fullWidth || matchButtonSize) &&
								css({ height: '36px' }),
						]}
					>
						{!isMobileView ? (
							<button
								className="aa-ClearButton"
								css={[aaClearButtonStyle, aiEnabled && aiEnabledClearButtonCss]}
								data-testid="search-close-button"
								aria-label={intl.formatMessage({
									id: 'globalnav.search.close-button',
									defaultMessage: 'Close search input field',
								})}
								title="Close"
								type="reset"
								onClick={(e) => {
									e.preventDefault();
									if (setIsOpenSearch) {
										setIsOpenSearch(false);
										lastTrackedQueryRef.current = '';
										createAnalyticsEvent({
											eventName: 'searchClose',
											eventType: 'ui',
											action: 'clicked',
											actionSubject: 'button',
											actionSubjectId: 'searchCloseButtonDesktop',
											source: 'globalNav',
											attributes: {
												product: 'search',
												pageUrl: window.location.href,
												pagePath: window.location.pathname,
											},
										}).fire();
									}
								}}
							>
								<Cross size="20" css={aaCrossIconStyle} />
							</button>
						) : null}
					</div>
				</form>

				<HiddenAriaLiveAnnouncement>{dropdownAnnouncement}</HiddenAriaLiveAnnouncement>

				{autocompleteState.isOpen && (
					<AutoCompleteResultsDropdown
						isMobileView={isMobileView}
						panelRef={panelRef}
						autocompleteState={autocompleteState}
						autocomplete={autocomplete}
						aiEnabled={aiEnabled}
					>
						<div className="aa-PanelLayout" css={[aaPanelLayoutStyle]} data-testid="aa-PanelLayout">
							{autocompleteState.collections.map(
								(collection: AutocompleteCollection<ContentHit>, index: number) => {
									const { source, items } = collection;

									return (
										<section key={`source-${index}`} className="aa-Source" css={[aaSourceStyle]}>
											{items.length > 0 && (
												<ul
													className="aa-List"
													css={[aaListStyle]}
													{...autocomplete.getListProps()}
													key={`list-${items.length}`}
												>
													{items.slice(0, isMobileView ? 5 : items.length).map((item) => {
														if (source.sourceId === 'ai-assistant') {
															return (
																<AutoCompleteSearchAIResult
																	key={item.objectID}
																	query={autocompleteState.query}
																	autocomplete={autocomplete}
																	source={source}
																	item={item}
																/>
															);
														}

														return handleRenderAutocompleteResult(item, source);
													})}
												</ul>
											)}
										</section>
									);
								},
							)}
						</div>
						{aiEnabled && (
							<div
								onMouseEnter={() => {
									autocomplete.setActiveItemId(null);
								}}
								onFocus={() => {
									autocomplete.setActiveItemId(null);
								}}
							>
								<Link
									css={seeAllButtonStyle}
									text={intl.formatMessage({
										id: 'globalnav.searchresult.see-all',
										defaultMessage: 'See all',
									})}
									href={'/search?q=' + autocompleteState.query}
									type={'primary'}
									dataEventProps={{
										'data-product-key': 'search',
										'data-event': 'clicked',
										'data-event-component': 'linkButton',
										'data-event-container': 'globalNavSearchResult',
										'data-label-english': 'See all',
									}}
									// ariaLabel prop does not satisfy eslint@atlassian/a11y/anchor-has-content rule
									aria-label={intl.formatMessage({
										id: 'globalnav.searchresult.see-all',
										defaultMessage: 'See all',
									})}
								/>
							</div>
						)}
					</AutoCompleteResultsDropdown>
				)}
			</div>
		</TypographyStyles>
	);
};

export default AutoCompleteSearchBar;
